import React from "react";
import { KTMScreen } from "ktm-ui";
import { Table, Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import axios from "axios";
import fileDownload from "js-file-download";
import toastr from "toastr";
import { SquarePen, ArrowLeft, FilePlus2, CircleDollarSign, Search, RefreshCw, Info, ShieldCheck } from "lucide-react";

// pad eft dashboard backed by /api/pad

const slate = "#64748b";
const muted = "#94a3b8";
const ink = "#1e293b";
const line = "#e2e8f0";

const money = (n: number) =>
    "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const asUtc = (v: string) => new Date(/[zZ]$|[+-]\d\d:?\d\d$/.test(v) ? v : v + "Z");

const fmtDate = (v?: string) =>
    v
        ? asUtc(v).toLocaleString("en-US", { timeZone: "America/Edmonton", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) + " MT"
        : "";

const fmtDay = (v?: string) =>
    v ? asUtc(v).toLocaleDateString("en-US", { timeZone: "America/Edmonton", month: "short", day: "numeric", year: "numeric" }) : "";

const FILE_STATUSES = ["pending", "notified", "uploaded", "complete", "void"];
const ENTRY_STATUSES = ["pending", "failed", "suspended", "adjust", "complete", "void"];

const statusBadge = (status: string) => {
    const map: { [k: string]: string } = {
        uploaded: "bg-primary text-white",
        complete: "bg-success text-white",
        notified: "bg-info text-white",
        pending: "bg-warning text-dark",
        void: "bg-secondary text-white",
        failed: "bg-danger text-white",
        suspended: "bg-warning text-dark",
        adjust: "bg-info text-white",
    };
    const cls = map[(status || "").toLowerCase()] || "bg-secondary text-white";
    return (
        <span className={`badge rounded-pill ${cls} px-3 py-1`} style={{ textTransform: "capitalize", fontWeight: 500 }}>
            {status}
        </span>
    );
};

const Stat = ({ label, value }: { label: string; value: any }) => (
    <div>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, color: muted, fontWeight: 700 }}>{label}</div>
        <div style={{ fontSize: 16, color: "#334155", fontWeight: 600, marginTop: 3 }}>{value}</div>
    </div>
);

const PageHeader = ({ title, subtitle, right }: { title: string; subtitle?: string; right?: any }) => (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", borderBottom: `1px solid ${line}`, paddingBottom: 16, marginBottom: 22 }}>
        <div>
            <h4 style={{ color: ink, fontWeight: 600, margin: 0, fontSize: 20 }}>{title}</h4>
            {subtitle && <div style={{ color: muted, fontSize: 13, marginTop: 4 }}>{subtitle}</div>}
        </div>
        {right}
    </div>
);

const LOG_COLORS: { [k: string]: { bg: string; fg: string } } = {
    generated: { bg: "#eff6ff", fg: "#2563eb" },
    notified: { bg: "#eff6ff", fg: "#2563eb" },
    uploaded: { bg: "#eff6ff", fg: "#2563eb" },
    workflow: { bg: "#eff6ff", fg: "#2563eb" },
    funded: { bg: "#f0fdf4", fg: "#16a34a" },
    rejected: { bg: "#fef2f2", fg: "#dc2626" },
    suspended: { bg: "#fffbeb", fg: "#d97706" },
    skipped: { bg: "#f1f5f9", fg: "#64748b" },
};

// log action -> badge color
const ACTION_BADGE: { [k: string]: string } = {
    CreateFile: "generated",
    CreatePad: "generated",
    RebuildFile: "generated",
    RebuildPad: "generated",
    WithdrawalNotification: "notified",
    FailureNotification: "rejected",
    SuspendNotification: "suspended",
    UpdateEft: "workflow",
    UpdatePad: "workflow",
    ApplyFunding: "funded",
    ApplyFundingTrusted: "funded",
    ApplyFundingError: "rejected",
    BuildPolicySkipped: "skipped",
    BuildPolicyError: "rejected",
    DownloadFile: "uploaded",
    // pa self-serve actions (per-policy activity list)
    SelfServeEnroll: "funded",
    UpdateBanking: "workflow",
    UpdateFloat: "workflow",
    UpdatePadConfig: "workflow",
    CancelRequest: "suspended",
};

const card: React.CSSProperties = {
    background: "#fff",
    border: `1px solid ${line}`,
    borderRadius: 12,
    padding: "20px 24px 12px",
    marginBottom: 28,
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
};

const FlagHeader = ({ label, tip }: { label: string; tip: string }) => (
    <th className="text-center">
        <OverlayTrigger placement="top" overlay={<Tooltip id={`tip-${label}`}>{tip}</Tooltip>}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "help" }}>
                {label} <Info size={13} style={{ color: muted }} />
            </span>
        </OverlayTrigger>
    </th>
);

const PAGE_SIZE = 25;

const Pager = ({ page, total, onPage }: { page: number; total: number; onPage: (p: number) => void }) => {
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (pages <= 1) return null;
    return (
        <div className="d-flex align-items-center justify-content-end gap-2" style={{ marginTop: 4, marginBottom: 8 }}>
            <span style={{ color: muted, fontSize: 13 }}>
                {page * PAGE_SIZE + 1}&ndash;{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </span>
            <button className="btn btn-outline-secondary btn-sm" disabled={page === 0} onClick={() => onPage(page - 1)}>
                &lsaquo;
            </button>
            <button className="btn btn-outline-secondary btn-sm" disabled={page >= pages - 1} onClick={() => onPage(page + 1)}>
                &rsaquo;
            </button>
        </div>
    );
};

const padPolicyBadge = (status: string) => {
    const map: { [k: string]: string } = {
        active: "bg-success text-white",
        suspended: "bg-danger text-white",
        paused: "bg-warning text-dark",
        off: "bg-secondary text-white",
    };
    return (
        <span className={`badge rounded-pill ${map[status]} px-3 py-1`} style={{ textTransform: "capitalize", fontWeight: 500 }}>
            {status}
        </span>
    );
};

const logBadge = (action: string) => {
    const c = LOG_COLORS[ACTION_BADGE[action] || "skipped"];
    return (
        <span style={{ display: "inline-block", background: c.bg, color: c.fg, fontWeight: 600, fontSize: 12, padding: "3px 10px", borderRadius: 999, whiteSpace: "nowrap" }}>
            {action}
        </span>
    );
};

export default KTMScreen(
    { name: "paddashboard" },
    class extends React.Component<any, any> {
        state = {
            view: "list", // list | detail
            files: [] as any[],
            loadingFiles: true,
            policies: [] as any[],
            policyTotal: 0,
            loadingPolicies: true,
            policyFilter: "all",
            policySearch: "",
            filePage: 0,
            policyPage: 0,
            editConfig: null as any, // { policy, floatAmount, capWithdrawal }
            savingFlags: null as string | null,
            configLogs: null as any[] | null, // per-policy audit trail shown in the edit modal
            entrySearch: "", // client-side filter within a file's entries
            entryStatusFilter: "all",
            logPage: 0,
            search: "",
            statusFilter: "all",
            file: null as any,
            entries: [] as any[],
            logs: [] as any[],
            loadingDetail: false,
            notes: "",
            busyAction: null as string | null,
            editEntry: null as any,
            editStatus: "pending",
            editNotes: "",
            confirm: null as any, // { title, body, action, actionLabel, variant }
        };

        askConfirm = (title: string, body: any, action: () => void, actionLabel = "Submit", variant = "primary") => {
            this.setState({ confirm: { title, body, action, actionLabel, variant } });
        };

        renderConfirmModal() {
            const c = this.state.confirm;
            if (!c) return null;
            const close = () => this.setState({ confirm: null });
            return (
                <Modal show onHide={close}>
                    <Modal.Header closeButton>
                        <Modal.Title>{c.title}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>{c.body}</Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={close}>
                            Cancel
                        </Button>
                        <Button
                            variant={c.variant}
                            onClick={() => {
                                this.setState({ confirm: null });
                                c.action();
                            }}
                        >
                            {c.actionLabel}
                        </Button>
                    </Modal.Footer>
                </Modal>
            );
        }

        componentDidMount() {
            this.loadFiles();
            this.loadPolicies();
        }

        policySearchTimer: any = null;

        loadPolicies = () => {
            const { policyPage, policySearch, policyFilter } = this.state;
            this.setState({ loadingPolicies: true });
            return axios
                .get("/api/pad/policies", {
                    params: { skip: policyPage * PAGE_SIZE, take: PAGE_SIZE, search: policySearch.trim(), status: policyFilter },
                })
                .then((res) =>
                    this.setState({ policies: res.data.policies || [], policyTotal: res.data.total || 0, loadingPolicies: false })
                )
                .catch((err) => {
                    this.setState({ loadingPolicies: false });
                    toastr.error(err.response?.data || "Failed to load PAD policies", "Error");
                });
        };

        onPolicySearch = (value: string) => {
            this.setState({ policySearch: value, policyPage: 0 });
            clearTimeout(this.policySearchTimer);
            this.policySearchTimer = setTimeout(this.loadPolicies, 300);
        };

        padPolicyStatus = (p: any) => {
            if (!p.enablePad || !p.isPadOn) return "off";
            if (p.isSuspended) return "suspended";
            if (p.isPaused) return "paused";
            return "active";
        };

        toggleFlag = (p: any, field: string, label: string) => {
            const value = !p[field];
            const flags = { [field]: value };
            const disabling =
                (field === "isSuspended" && value) || (field === "isPaused" && value) || (field === "isPadOn" && !value);
            if (disabling) {
                this.askConfirm(
                    label,
                    <span>
                        This will exclude <strong>{p.policyName}</strong> from future PAD EFT builds. Would you like to
                        proceed?
                    </span>,
                    () => this.doUpdateFlags(p, flags),
                    label,
                    "danger"
                );
            } else {
                this.doUpdateFlags(p, flags);
            }
        };

        openConfig = (p: any) => {
            this.setState({
                editConfig: {
                    policy: p,
                    floatAmount: String(p.floatAmount ?? 0),
                    capWithdrawal: p.capWithdrawal,
                },
                configLogs: null,
            });
            axios
                .get(`/api/pad/policies/${p.policyId}/logs`)
                .then((res) => {
                    if (res.data.success) this.setState({ configLogs: res.data.logs });
                })
                .catch(() => this.setState({ configLogs: [] }));
        };

        setConfigField = (field: string, value: string) => {
            this.setState({ editConfig: { ...this.state.editConfig, [field]: value } });
        };

        saveConfig = () => {
            const c = this.state.editConfig;
            const flt = parseFloat(c.floatAmount);
            if (isNaN(flt) || flt < 0) {
                toastr.warning("Float must be zero or more", "Invalid");
                return;
            }
            axios
                .post(`/api/pad/policies/${c.policy.policyId}/config`, {
                    floatAmount: flt,
                    capWithdrawal: c.capWithdrawal,
                })
                .then(() => {
                    toastr.success(`PAD settings updated for ${c.policy.policyName}`, "Success");
                    this.setState({ editConfig: null });
                    this.loadPolicies();
                })
                .catch((err) =>
                    toastr.error(err.response?.data?.message || err.response?.data || "Failed to update PAD settings", "Error")
                );
        };

        doUpdateFlags = (p: any, flags: any) => {
            this.setState({ savingFlags: p.policyId });
            axios
                .post(`/api/pad/policies/${p.policyId}/flags`, flags)
                .then(() => {
                    toastr.success(`PAD settings updated for ${p.policyName}`, "Success");
                    return this.loadPolicies();
                })
                .catch((err) => toastr.error(err.response?.data || "Failed to update PAD settings", "Error"))
                .finally(() => this.setState({ savingFlags: null }));
        };

        loadFiles = () => {
            this.setState({ loadingFiles: true });
            axios
                .get("/api/pad/files")
                .then((res) => this.setState({ files: res.data.files || [], loadingFiles: false }))
                .catch((err) => {
                    this.setState({ loadingFiles: false });
                    toastr.error(err.response?.data || "Failed to load EFT files", "Error");
                });
        };

        loadDetail = (id: string) => {
            this.setState({ loadingDetail: true });
            Promise.all([axios.get(`/api/pad/files/${id}`), axios.get(`/api/pad/files/${id}/logs`)])
                .then(([fileRes, logsRes]) =>
                    this.setState({
                        file: fileRes.data.file,
                        entries: fileRes.data.entries || [],
                        logs: logsRes.data.logs || [],
                        notes: fileRes.data.file?.notes || "",
                        loadingDetail: false,
                    })
                )
                .catch((err) => {
                    this.setState({ loadingDetail: false });
                    toastr.error(err.response?.data || "Failed to load file", "Error");
                });
        };

        openDetail = (file: any) => {
            this.setState({ view: "detail", file, entries: [], logs: [], notes: file.notes || "", entrySearch: "", entryStatusFilter: "all", logPage: 0 });
            this.loadDetail(file.id);
        };

        backToList = () => {
            this.setState({ view: "list", file: null });
            this.loadFiles();
        };

        generateFile = () => {
            this.askConfirm(
                "Generate EFT File",
                "A debit entry is created for every eligible policy whose funds fall short of its float plus approved claims. Would you like to proceed?",
                this.doGenerateFile,
                "Generate"
            );
        };

        maybeStillRunning = (err: any) =>
            !err.response || err.code === "ECONNABORTED" || err.response.status === 502 || err.response.status === 504;

        doGenerateFile = () => {
            this.setState({ busyAction: "build" });
            axios
                .post("/api/pad/build")
                .then((res) => {
                    this.setState({ busyAction: null });
                    if (res.data.success) {
                        toastr.success(`File ${res.data.fileNum} created — ${res.data.entryCount} entries, ${money(res.data.totalAmount)} (${res.data.skipped} skipped)`, "Success");
                    } else {
                        toastr.warning(res.data.message, "Not created");
                    }
                    this.loadFiles();
                })
                .catch((err) => {
                    this.setState({ busyAction: null });
                    if (this.maybeStillRunning(err)) {
                        toastr.warning("The build is taking longer than expected and may still be running. Check the file list again in a minute before retrying.", "Still running?");
                        this.loadFiles();
                    } else {
                        toastr.error(err.response?.data || "Failed to generate EFT file", "Error");
                    }
                });
        };

        rebuildFile = () => {
            const f = this.state.file;
            this.askConfirm(
                "Rebuild File",
                <span>
                    This will void file <strong>{f.fileNum}</strong> and all its entries, then regenerate pending/adjust
                    entries under a new file number. Would you like to proceed?
                </span>,
                this.doRebuildFile,
                "Rebuild",
                "danger"
            );
        };

        doRebuildFile = () => {
            const f = this.state.file;
            this.setState({ busyAction: "rebuild" });
            axios
                .post(`/api/pad/files/${f.id}/rebuild`)
                .then((res) => {
                    this.setState({ busyAction: null });
                    if (res.data.success) {
                        toastr.success(`File ${res.data.fileNum} created — ${res.data.entryCount} entries, ${money(res.data.totalAmount)}`, "Rebuilt");
                        this.backToList();
                    } else {
                        toastr.warning(res.data.message, "Not rebuilt");
                        this.loadDetail(f.id);
                    }
                })
                .catch((err) => {
                    this.setState({ busyAction: null });
                    if (this.maybeStillRunning(err)) {
                        toastr.warning("The rebuild is taking longer than expected and may still be running. Check the file list again in a minute before retrying.", "Still running?");
                        this.backToList();
                    } else {
                        toastr.error(err.response?.data || "Failed to rebuild file", "Error");
                        this.loadDetail(f.id);
                    }
                });
        };

        setFileStatus = (status: string) => {
            if (status === "void") {
                this.askConfirm(
                    "Void File",
                    <span>
                        Void file <strong>{this.state.file.fileNum}</strong>? Would you like to proceed?
                    </span>,
                    () => this.doSetFileStatus(status),
                    "Void",
                    "danger"
                );
                return;
            }
            this.doSetFileStatus(status);
        };

        doSetFileStatus = (status: string) => {
            const f = this.state.file;
            axios
                .post(`/api/pad/files/${f.id}/status`, { value: status })
                .then(() => {
                    toastr.success("Status updated", "Success");
                    this.loadDetail(f.id);
                })
                .catch((err) => toastr.error(err.response?.data || "Failed to update status", "Error"));
        };

        saveNotes = () => {
            const f = this.state.file;
            axios
                .post(`/api/pad/files/${f.id}/notes`, { value: this.state.notes })
                .then(() => toastr.success("Notes saved", "Success"))
                .catch((err) => toastr.error(err.response?.data || "Failed to save notes", "Error"));
        };

        sendNotification = () => {
            const f = this.state.file;
            this.askConfirm(
                "Send Withdrawal Notifications",
                <span>
                    This will email withdrawal notifications to the plan admins of all{" "}
                    <strong>{this.state.entries.length}</strong> companies in file <strong>{f.fileNum}</strong>. Would
                    you like to proceed?
                </span>,
                this.doSendNotification,
                "Send"
            );
        };

        doSendNotification = () => {
            const f = this.state.file;
            this.setState({ busyAction: "notify" });
            axios
                .post(`/api/pad/files/${f.id}/notify`)
                .then((res) => {
                    this.setState({ busyAction: null });
                    toastr.success(`Notifications sent: ${res.data.sent}${res.data.failed ? `, failed: ${res.data.failed}` : ""}`, "Success");
                    this.loadDetail(f.id);
                })
                .catch((err) => {
                    this.setState({ busyAction: null });
                    if (this.maybeStillRunning(err)) {
                        toastr.warning("Sending may still be running in the background. Refresh this file in a minute to see which notifications went out before retrying.", "Still running?");
                        this.loadDetail(f.id);
                    } else {
                        toastr.error(err.response?.data || "Failed to send notifications", "Error");
                    }
                });
        };

        downloadFile = () => {
            // fetch as blob; a plain navigation isn't forwarded by the dev proxy
            const f = this.state.file;
            this.setState({ busyAction: "download" });
            axios
                .get(`/api/pad/files/${f.id}/download`, { responseType: "blob" })
                .then((res) => {
                    this.setState({ busyAction: null });
                    fileDownload(res.data, f.fileName);
                    toastr.success("EFT file downloaded", "Success");
                })
                .catch((err) => {
                    this.setState({ busyAction: null });
                    toastr.error(err.response?.data?.message || "Failed to download EFT file", "Error");
                });
        };

        applyFundingAll = () => {
            const f = this.state.file;
            this.askConfirm(
                "Apply Funding to All",
                <span>
                    Each pending, never-funded entry in file <strong>{f.fileNum}</strong> will post a PAD cash receipt
                    to its policy ledger. Would you like to proceed?
                </span>,
                this.doApplyFundingAll,
                "Apply Funding"
            );
        };

        doApplyFundingAll = () => {
            const f = this.state.file;
            this.setState({ busyAction: "apply" });
            axios
                .post(`/api/pad/files/${f.id}/applyfunding`)
                .then((res) => {
                    this.setState({ busyAction: null });
                    const skipped = res.data.skipped || [];
                    const alreadyFunded = skipped.filter((s: any) => s.reason === "Already funded").length;
                    const rest = skipped.length - alreadyFunded;
                    const parts = [`Funding applied to ${res.data.applied} entries`];
                    if (alreadyFunded) parts.push(`${alreadyFunded} already funded`);
                    if (rest) parts.push(`${rest} skipped`);
                    toastr.success(parts.join("; "), "Success");
                    this.loadDetail(f.id);
                })
                .catch((err) => {
                    this.setState({ busyAction: null });
                    if (this.maybeStillRunning(err)) {
                        toastr.warning("Funding may still be running in the background. Refresh this file in a minute to see funded entries before retrying.", "Still running?");
                        this.loadDetail(f.id);
                    } else {
                        toastr.error(err.response?.data || "Failed to apply funding", "Error");
                    }
                });
        };

        applyFundingTrusted = () => {
            const f = this.state.file;
            const fundableTrusted = this.state.entries.filter(
                (e: any) => e.trusted && e.status === "pending" && !e.fundedAt
            );
            this.askConfirm(
                "Apply Credit to Trusted Policies",
                <span>
                    <strong>{fundableTrusted.length}</strong> of the <strong>{this.state.entries.length}</strong>{" "}
                    companies in file <strong>{f.fileNum}</strong> qualify as trusted (no failures ever, 25+ successful
                    pulls, renewed at least once). Each of their pending, never-funded entries will post a PAD cash
                    receipt to its policy ledger. Would you like to proceed?
                </span>,
                this.doApplyFundingTrusted,
                "Apply Credit"
            );
        };

        doApplyFundingTrusted = () => {
            const f = this.state.file;
            this.setState({ busyAction: "applyTrusted" });
            axios
                .post(`/api/pad/files/${f.id}/applyfundingtrusted`)
                .then((res) => {
                    this.setState({ busyAction: null });
                    const skipped = res.data.skipped || [];
                    const alreadyFunded = skipped.filter((s: any) => s.reason === "Already funded").length;
                    const notTrusted = skipped.filter((s: any) => s.reason === "Not a trusted policy").length;
                    const rest = skipped.length - alreadyFunded - notTrusted;
                    const parts = [`Trusted credits applied to ${res.data.applied} entries`];
                    if (notTrusted) parts.push(`${notTrusted} not trusted`);
                    if (alreadyFunded) parts.push(`${alreadyFunded} already funded`);
                    if (rest) parts.push(`${rest} skipped`);
                    toastr.success(parts.join("; "), "Success");
                    this.loadDetail(f.id);
                })
                .catch((err) => {
                    this.setState({ busyAction: null });
                    if (this.maybeStillRunning(err)) {
                        toastr.warning("Funding may still be running in the background. Refresh this file in a minute to see funded entries before retrying.", "Still running?");
                        this.loadDetail(f.id);
                    } else {
                        toastr.error(err.response?.data || "Failed to apply trusted credits", "Error");
                    }
                });
        };

        applyEntryFunding = (entry: any) => {
            this.askConfirm(
                "Apply KTM Credit",
                <span>
                    This will post a PAD cash receipt of <strong>{money(entry.amount)}</strong> to the ledger of{" "}
                    <strong>{entry.policyName}</strong>. Would you like to proceed?
                </span>,
                () => this.doApplyEntryFunding(entry),
                "Apply Credit"
            );
        };

        doApplyEntryFunding = (entry: any) => {
            axios
                .post(`/api/pad/entries/${entry.id}/applyfunding`)
                .then((res) => {
                    if (res.data.success) {
                        toastr.success("KTM credit applied", "Success");
                    } else {
                        toastr.warning(res.data.message, "Not applied");
                    }
                    this.loadDetail(this.state.file.id);
                })
                .catch((err) => toastr.error(err.response?.data || "Failed to apply funding", "Error"));
        };

        openEdit = (entry: any) => {
            this.setState({ editEntry: entry, editStatus: entry.status, editNotes: entry.notes || "" });
        };

        saveEntry = () => {
            const e = this.state.editEntry;
            axios
                .post(`/api/pad/entries/${e.id}`, { status: this.state.editStatus, notes: this.state.editNotes })
                .then(() => {
                    toastr.success("Entry updated", "Success");
                    this.setState({ editEntry: null });
                    this.loadDetail(this.state.file.id);
                })
                .catch((err) => toastr.error(err.response?.data || "Failed to update entry", "Error"));
        };

        sendEntryNotice = (kind: "withdrawal" | "failure" | "suspend") => {
            const e = this.state.editEntry;
            this.askConfirm(
                kind === "withdrawal" ? "Resend Withdrawal Notification" : kind === "failure" ? "Send Failure Notice" : "Send Suspend Notice",
                <span>
                    This will email a PAD {kind} notice to the plan admins of <strong>{e.policyName}</strong>. Would you
                    like to proceed?
                </span>,
                () => this.doSendEntryNotice(kind),
                "Send"
            );
        };

        doSendEntryNotice = (kind: "withdrawal" | "failure" | "suspend") => {
            const e = this.state.editEntry;
            axios
                .post(`/api/pad/entries/${e.id}/notify${kind}`)
                .then((res) => {
                    if (res.data.success) {
                        toastr.success(kind === "withdrawal" ? "PAD withdrawal notification sent" : `PAD ${kind} notice sent`, "Success");
                    } else {
                        toastr.warning(res.data.message, "Not sent");
                    }
                })
                .catch((err) => toastr.error(err.response?.data || "Failed to send notice", "Error"));
        };

        hasAdjustEntries = () => this.state.entries.some((e: any) => e.status === "adjust");

        workflowStep(num: number, desc: string, label: string, status: string, action?: { label: string; onClick: () => void; busyKey: string }) {
            const checked = this.state.file?.status === status;
            const gated = this.hasAdjustEntries();
            return (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 13, color: slate, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span>
                            {num}. {desc}
                        </span>
                        {action && (
                            <button
                                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                                style={{ fontSize: 12, padding: "2px 10px" }}
                                disabled={!!this.state.busyAction || gated}
                                onClick={action.onClick}
                            >
                                {this.state.busyAction === action.busyKey && (
                                    <span className="spinner-border spinner-border-sm" role="status" style={{ width: 12, height: 12 }} />
                                )}
                                {action.label}
                            </button>
                        )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5 }}>
                        <input type="radio" name="wf-status" checked={checked} disabled={gated} onChange={() => this.setFileStatus(status)} />
                        <span style={{ color: "#334155" }}>{label}</span>
                    </div>
                </div>
            );
        }

        renderList() {
            const q = this.state.search.trim().toLowerCase();
            const filtered = this.state.files.filter(
                (f) =>
                    (this.state.statusFilter === "all" || f.status === this.state.statusFilter) &&
                    (q === "" || String(f.fileNum).includes(q) || (f.fileName || "").toLowerCase().includes(q))
            );
            const paged = filtered.slice(this.state.filePage * PAGE_SIZE, (this.state.filePage + 1) * PAGE_SIZE);
            return (
                <div>
                    <div style={card}>
                    <PageHeader
                        title="EFT Tasks"
                        subtitle="Generate and manage PAD withdrawal batches"
                        right={
                            <div className="d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
                                <div className="input-group" style={{ width: 210 }}>
                                    <span className="input-group-text bg-white text-muted">
                                        <Search size={15} />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search file no."
                                        value={this.state.search}
                                        onChange={(e) => this.setState({ search: e.target.value, filePage: 0 })}
                                    />
                                </div>
                                <select
                                    className="form-select"
                                    style={{ width: 160 }}
                                    value={this.state.statusFilter}
                                    onChange={(e) => this.setState({ statusFilter: e.target.value, filePage: 0 })}
                                >
                                    <option value="all">All statuses</option>
                                    {FILE_STATUSES.map((s) => (
                                        <option key={s} value={s}>
                                            {s.charAt(0).toUpperCase() + s.slice(1)}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    className="btn btn-primary d-inline-flex align-items-center gap-2 text-nowrap"
                                    disabled={!!this.state.busyAction}
                                    onClick={this.generateFile}
                                >
                                    {this.state.busyAction === "build" ? <span className="spinner-border spinner-border-sm" role="status" /> : <FilePlus2 size={16} />} Generate EFT File
                                </button>
                            </div>
                        }
                    />
                    <Table hover responsive className="align-middle">
                        <thead>
                            <tr style={{ color: slate }}>
                                <th></th>
                                <th>File No.</th>
                                <th>Status</th>
                                <th>File Name</th>
                                <th>Total Companies</th>
                                <th>Total Amount</th>
                                <th>Date</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {paged.map((f, i) => (
                                <tr key={f.id}>
                                    <td style={{ color: muted }}>{this.state.filePage * PAGE_SIZE + i + 1}</td>
                                    <td>{f.fileNum}</td>
                                    <td>{statusBadge(f.status)}</td>
                                    <td>{f.fileName}</td>
                                    <td>{f.entryCount}</td>
                                    <td>{money(f.totalAmount)}</td>
                                    <td>{fmtDate(f.createdAt)}</td>
                                    <td className="text-end">
                                        <button className="btn btn-primary btn-sm" onClick={() => this.openDetail(f)}>
                                            Details
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {!this.state.loadingFiles && filtered.length === 0 && (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: "center", color: muted, padding: "24px 0" }}>
                                        {this.state.files.length === 0 ? "No EFT files yet." : "No EFT files match your search."}
                                    </td>
                                </tr>
                            )}
                            {this.state.loadingFiles && (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: "center", color: muted, padding: "24px 0" }}>
                                        Loading…
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                    <Pager page={this.state.filePage} total={filtered.length} onPage={(p) => this.setState({ filePage: p })} />
                    </div>
                    {this.renderPoliciesCard()}
                </div>
            );
        }

        flagSwitch(p: any, field: string, label: string) {
            return (
                <td className="text-center">
                    <div className="form-check form-switch d-inline-block">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={p[field]}
                            disabled={this.state.savingFlags === p.policyId}
                            onChange={() => this.toggleFlag(p, field, label)}
                        />
                    </div>
                </td>
            );
        }

        renderPoliciesCard() {
            const paged = this.state.policies;
            return (
                <div style={card}>
                    <PageHeader
                        title="PAD Policies"
                        subtitle="Enrolled policies and their PAD settings. All changes are logged."
                        right={
                            <div className="d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
                                <div className="input-group" style={{ width: 210 }}>
                                    <span className="input-group-text bg-white text-muted">
                                        <Search size={15} />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search company"
                                        value={this.state.policySearch}
                                        onChange={(e) => this.onPolicySearch(e.target.value)}
                                    />
                                </div>
                                <select
                                    className="form-select"
                                    style={{ width: 160 }}
                                    value={this.state.policyFilter}
                                    onChange={(e) => this.setState({ policyFilter: e.target.value, policyPage: 0 }, this.loadPolicies)}
                                >
                                    <option value="all">All statuses</option>
                                    <option value="active">Active</option>
                                    <option value="suspended">Suspended</option>
                                    <option value="paused">Paused</option>
                                    <option value="off">Off</option>
                                </select>
                            </div>
                        }
                    />
                    <Table hover responsive className="align-middle">
                        <thead>
                            <tr style={{ color: slate }}>
                                <th></th>
                                <th>Company</th>
                                <th>Float</th>
                                <th>Banking</th>
                                <th>Status</th>
                                <FlagHeader label="PAD On" tip="Include this policy in PAD builds. Off means every build skips it." />
                                <FlagHeader label="Suspended" tip="Set automatically when an EFT fails. Builds skip the policy until this is cleared." />
                                <FlagHeader label="Paused" tip="Paused at the company's request. Builds skip the policy until this is cleared." />
                                <FlagHeader label="Cap Withdrawal" tip="Limit each withdrawal to the float amount." />
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {paged.map((p, i) => (
                                <tr key={p.policyId}>
                                    <td style={{ color: muted }}>{this.state.policyPage * PAGE_SIZE + i + 1}</td>
                                    <td>
                                        {p.policyName} <span style={{ color: muted, fontSize: 12 }}>· {p.policyNo}</span>
                                    </td>
                                    <td>{money(p.floatAmount)}</td>
                                    <td>
                                        {p.hasBanking ? (
                                            <span style={{ fontSize: 13 }}>
                                                {p.bankNo}-{p.transitNo}-{p.bankAccountMasked}
                                                {p.bankingUpdatedAt && (
                                                    <div style={{ fontSize: 11, color: muted }}>updated {fmtDay(p.bankingUpdatedAt)}</div>
                                                )}
                                            </span>
                                        ) : (
                                            <span style={{ color: "#dc2626", fontSize: 13 }}>Missing</span>
                                        )}
                                    </td>
                                    <td>{padPolicyBadge(this.padPolicyStatus(p))}</td>
                                    {this.flagSwitch(p, "isPadOn", "Turn PAD Off")}
                                    {this.flagSwitch(p, "isSuspended", "Suspend PAD")}
                                    {this.flagSwitch(p, "isPaused", "Pause PAD")}
                                    {this.flagSwitch(p, "capWithdrawal", "Cap Withdrawal")}
                                    <td className="text-end">
                                        <button
                                            className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                                            onClick={() => this.openConfig(p)}
                                        >
                                            <SquarePen size={15} /> Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {!this.state.loadingPolicies && paged.length === 0 && (
                                <tr>
                                    <td colSpan={10} style={{ textAlign: "center", color: muted, padding: "24px 0" }}>
                                        {this.state.policySearch === "" && this.state.policyFilter === "all"
                                            ? "No policies enrolled in PAD yet."
                                            : "No enrolled policies match. Policies appear here once they are enrolled in PAD."}
                                    </td>
                                </tr>
                            )}
                            {this.state.loadingPolicies && (
                                <tr>
                                    <td colSpan={10} style={{ textAlign: "center", color: muted, padding: "24px 0" }}>
                                        Loading…
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                    <Pager page={this.state.policyPage} total={this.state.policyTotal} onPage={(p) => this.setState({ policyPage: p }, this.loadPolicies)} />
                </div>
            );
        }

        renderConfigModal() {
            const c = this.state.editConfig;
            if (!c) return null;
            const close = () => this.setState({ editConfig: null });
            return (
                <Modal show onHide={close} centered>
                    <Modal.Header closeButton>
                        <Modal.Title style={{ fontSize: 17 }}>
                            {c.policy.policyName} <span style={{ color: muted, fontWeight: 400 }}>· PAD settings</span>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <label style={{ color: slate, fontSize: 13 }}>Float Amount</label>
                        <input
                            type="number"
                            min={0}
                            step={0.01}
                            className="form-control"
                            style={{ marginTop: 4, marginBottom: 14 }}
                            value={c.floatAmount}
                            onChange={(e) => this.setConfigField("floatAmount", e.target.value)}
                        />
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="cfg-cap-withdrawal"
                                checked={c.capWithdrawal}
                                onChange={() => this.setState({ editConfig: { ...c, capWithdrawal: !c.capWithdrawal } })}
                            />
                            <label className="form-check-label" htmlFor="cfg-cap-withdrawal" style={{ color: "#334155" }}>
                                Cap each withdrawal at the float amount
                            </label>
                        </div>
                        <div style={{ fontSize: 12, color: muted, marginTop: 10 }}>
                            Banking is managed by the plan admin. Changes take effect on the next EFT build.
                        </div>
                        <div style={{ marginTop: 16 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: slate, marginBottom: 6 }}>Recent activity</div>
                            <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #f1f5f9", borderRadius: 8, padding: "4px 12px" }}>
                                {this.state.configLogs === null && (
                                    <div style={{ color: muted, fontSize: 13, padding: "8px 0" }}>Loading…</div>
                                )}
                                {Array.isArray(this.state.configLogs) && this.state.configLogs.length === 0 && (
                                    <div style={{ color: muted, fontSize: 13, padding: "8px 0" }}>No recorded activity for this policy.</div>
                                )}
                                {Array.isArray(this.state.configLogs) &&
                                    this.state.configLogs.map((l: any) => (
                                        <div key={l.id} style={{ padding: "8px 0", borderBottom: "1px solid #f8fafc" }}>
                                            {logBadge(l.action)}
                                            {l.message && (
                                                <div style={{ fontSize: 12, color: "#334155", wordBreak: "break-word", marginTop: 3 }}>{l.message}</div>
                                            )}
                                            <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>
                                                {fmtDate(l.createdAt)} · {l.actor || "System"}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="btn btn-outline-secondary" onClick={close}>
                            Cancel
                        </button>
                        <button className="btn btn-primary" onClick={this.saveConfig}>
                            Save
                        </button>
                    </Modal.Footer>
                </Modal>
            );
        }

        renderEditModal() {
            const e = this.state.editEntry;
            if (!e) return null;
            return (
                <Modal show onHide={() => this.setState({ editEntry: null })} centered>
                    <Modal.Header closeButton>
                        <Modal.Title style={{ fontSize: 17 }}>
                            {e.policyName} <span style={{ color: muted, fontWeight: 400 }}>· {money(e.amount)}</span>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <label style={{ color: slate, fontSize: 13 }}>Status</label>
                        <select
                            className="form-select"
                            style={{ marginTop: 4, marginBottom: 4 }}
                            value={this.state.editStatus}
                            onChange={(ev) => this.setState({ editStatus: ev.target.value })}
                        >
                            {ENTRY_STATUSES.map((s) => (
                                <option key={s} value={s}>
                                    {s.charAt(0).toUpperCase() + s.slice(1)}
                                </option>
                            ))}
                        </select>
                        <div style={{ fontSize: 12, color: muted, marginBottom: 12 }}>
                            "Failed" suspends the policy's PAD (EFT failure); "Suspended" pauses it (company request). Future builds skip the policy either way.
                        </div>
                        <label style={{ color: slate, fontSize: 13 }}>Notes</label>
                        <textarea
                            className="form-control"
                            rows={3}
                            style={{ marginTop: 4, marginBottom: 14 }}
                            value={this.state.editNotes}
                            onChange={(ev) => this.setState({ editNotes: ev.target.value })}
                        />
                        <div className="d-flex gap-2 flex-wrap">
                            <button className="btn btn-outline-primary btn-sm" onClick={() => this.sendEntryNotice("withdrawal")}>
                                Resend withdrawal notification
                            </button>
                            <button className="btn btn-outline-danger btn-sm" onClick={() => this.sendEntryNotice("failure")}>
                                Send failure notice
                            </button>
                            <button className="btn btn-outline-warning btn-sm" onClick={() => this.sendEntryNotice("suspend")}>
                                Send suspend notice
                            </button>
                        </div>
                        {e.withdrawEmailLastSent && (
                            <div style={{ fontSize: 12, color: muted, marginTop: 6 }}>Withdrawal notification sent {fmtDate(e.withdrawEmailLastSent)}</div>
                        )}
                        {e.failureEmailLastSent && (
                            <div style={{ fontSize: 12, color: muted, marginTop: 6 }}>Failure/suspend notice sent {fmtDate(e.failureEmailLastSent)}</div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="btn btn-outline-secondary" onClick={() => this.setState({ editEntry: null })}>
                            Cancel
                        </button>
                        <button className="btn btn-primary" onClick={this.saveEntry}>
                            Save
                        </button>
                    </Modal.Footer>
                </Modal>
            );
        }

        renderDetail() {
            const f = this.state.file;
            const fundable = this.state.entries.filter((c: any) => c.status === "pending" && !c.fundedAt);
            const fundableTrusted = fundable.filter((c: any) => c.trusted);
            const notUploaded = f.status !== "uploaded";
            const q = this.state.entrySearch.trim().toLowerCase();
            const filteredEntries = this.state.entries.filter(
                (c) =>
                    (this.state.entryStatusFilter === "all" || c.status === this.state.entryStatusFilter) &&
                    (q === "" || (c.policyName || "").toLowerCase().includes(q) || (c.policyNo || "").toLowerCase().includes(q))
            );
            const entryFilterActive = q !== "" || this.state.entryStatusFilter !== "all";
            return (
                <div>
                    <div
                        onClick={this.backToList}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, color: muted, cursor: "pointer", marginBottom: 14 }}
                    >
                        <ArrowLeft size={18} /> Return
                    </div>

                    <div style={{ borderBottom: `1px solid ${line}`, paddingBottom: 18, marginBottom: 24 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                            <h3 style={{ color: ink, fontWeight: 600, margin: 0, fontSize: 22 }}>File No. {f.fileNum}</h3>
                            {statusBadge(f.status)}
                            <div style={{ marginLeft: "auto" }}>
                                <button
                                    className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                                    disabled={!!this.state.busyAction || f.status === "void"}
                                    onClick={this.rebuildFile}
                                >
                                    {this.state.busyAction === "rebuild" ? <span className="spinner-border spinner-border-sm" role="status" style={{ width: 14, height: 14 }} /> : <RefreshCw size={14} />} Rebuild File
                                </button>
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 48, flexWrap: "wrap" }}>
                            <Stat label="Generated" value={fmtDate(f.createdAt)} />
                            <Stat label="Total Companies" value={f.entryCount} />
                            <Stat label="Total Amount" value={money(f.totalAmount)} />
                            {f.withdrawEmailLastSent && <Stat label="Notified" value={fmtDate(f.withdrawEmailLastSent)} />}
                        </div>
                        {this.hasAdjustEntries() && f.status !== "void" && (
                            <div className="alert alert-warning d-flex align-items-center gap-2" style={{ marginTop: 16, marginBottom: 0, fontSize: 13, padding: "10px 14px" }}>
                                <Info size={16} />
                                <span>
                                    An adjustment has been requested in this file. Rebuild the file to regenerate it with
                                    the adjusted amounts. All other actions are disabled until then.
                                </span>
                            </div>
                        )}
                    </div>

                    <div style={{ background: "#f8fafc", border: `1px solid ${line}`, borderRadius: 12, padding: 24, display: "flex", gap: 32, flexWrap: "wrap", marginBottom: 32 }}>
                        <div style={{ flex: 1, minWidth: 260 }}>
                            <h4 style={{ color: slate, fontWeight: 600, marginBottom: 18, fontSize: 15 }}>Workflow Status</h4>
                            {this.workflowStep(1, "Pending process", "Pending", "pending")}
                            {this.workflowStep(2, "Send withdrawal notification.", "Notified Plan Admin", "notified", { label: "Send Notification", onClick: this.sendNotification, busyKey: "notify" })}
                            {this.workflowStep(3, "Download and save EFT file.", "Uploaded EFT file to TD", "uploaded", { label: "Download File", onClick: this.downloadFile, busyKey: "download" })}
                            {this.workflowStep(4, "Companies credited. Task complete.", "Complete", "complete")}
                        </div>
                        <div style={{ flex: 1, minWidth: 260 }}>
                            <label style={{ color: slate, fontSize: 13 }}>Notes</label>
                            <textarea
                                className="form-control"
                                rows={6}
                                placeholder="Enter notes here.."
                                style={{ marginTop: 6 }}
                                value={this.state.notes}
                                onChange={(e) => this.setState({ notes: e.target.value })}
                            />
                            <div className="text-end" style={{ marginTop: 12 }}>
                                <button className="btn btn-primary" onClick={this.saveNotes}>
                                    Save Notes
                                </button>
                            </div>
                        </div>
                    </div>

                    <PageHeader
                        title="PAD Companies"
                        subtitle={
                            entryFilterActive
                                ? `${filteredEntries.length} of ${this.state.entries.length} policies in this batch`
                                : `${this.state.entries.length} policies in this batch`
                        }
                        right={
                            <div className="text-end">
                                <div className="d-flex gap-2 justify-content-end">
                                    <button
                                        className="btn btn-primary d-inline-flex align-items-center gap-2 text-nowrap"
                                        disabled={!!this.state.busyAction || this.hasAdjustEntries() || notUploaded || fundableTrusted.length === 0}
                                        onClick={this.applyFundingTrusted}
                                    >
                                        {this.state.busyAction === "applyTrusted" ? <span className="spinner-border spinner-border-sm" role="status" /> : <ShieldCheck size={16} />} Apply Credit to Trusted Policies
                                    </button>
                                    <button
                                        className="btn btn-success d-inline-flex align-items-center gap-2 text-nowrap"
                                        disabled={!!this.state.busyAction || this.hasAdjustEntries() || notUploaded || fundable.length === 0}
                                        onClick={this.applyFundingAll}
                                    >
                                        {this.state.busyAction === "apply" ? <span className="spinner-border spinner-border-sm" role="status" /> : <CircleDollarSign size={16} />} Apply Funding to All
                                    </button>
                                </div>
                                <div style={{ fontSize: 12, color: muted, marginTop: 5 }}>
                                    {f.status === "void"
                                        ? "This file is void."
                                        : notUploaded
                                        ? "Crediting enables once the EFT file is marked as uploaded to TD."
                                        : fundable.length === 0
                                        ? "All entries in this file have been processed."
                                        : `Only pending entries that haven't been funded yet are credited. Failed, suspended, adjusted and voided entries are skipped. ${fundableTrusted.length} of ${this.state.entries.length} companies qualify as trusted.`}
                                </div>
                            </div>
                        }
                    />

                    <div className="d-flex align-items-center gap-2" style={{ marginBottom: 12 }}>
                        <input
                            type="text"
                            className="form-control"
                            style={{ width: 260 }}
                            placeholder="Search company or policy no"
                            value={this.state.entrySearch}
                            onChange={(e) => this.setState({ entrySearch: e.target.value })}
                        />
                        <select
                            className="form-select"
                            style={{ width: 160 }}
                            value={this.state.entryStatusFilter}
                            onChange={(e) => this.setState({ entryStatusFilter: e.target.value })}
                        >
                            <option value="all">All statuses</option>
                            {ENTRY_STATUSES.map((s) => (
                                <option key={s} value={s}>
                                    {s.charAt(0).toUpperCase() + s.slice(1)}
                                </option>
                            ))}
                        </select>
                        {entryFilterActive && (
                            <button
                                className="btn btn-link btn-sm text-decoration-none"
                                onClick={() => this.setState({ entrySearch: "", entryStatusFilter: "all" })}
                            >
                                Clear
                            </button>
                        )}
                    </div>

                    <Table hover responsive className="align-middle">
                        <thead>
                            <tr style={{ color: slate }}>
                                <th></th>
                                <th>Company</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Notes</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEntries.map((c, i) => (
                                <tr
                                    key={c.id}
                                    style={
                                        c.status === "void" || c.status === "suspended"
                                            ? { backgroundColor: "#fce7f3" }
                                            : c.status === "failed"
                                            ? { backgroundColor: "#fee2e2" }
                                            : undefined
                                    }
                                >
                                    <td style={{ color: muted }}>{i + 1}</td>
                                    <td>
                                        {c.policyName}{" "}
                                        {c.trusted && (
                                            <span title="Trusted policy: no failures ever, 25+ successful pulls, renewed at least once">
                                                <ShieldCheck size={13} style={{ color: "#2563eb", verticalAlign: "-2px" }} />
                                            </span>
                                        )}{" "}
                                        <span style={{ color: muted, fontSize: 12 }}>· {c.policyNo}</span>
                                    </td>
                                    <td>{money(c.amount)}</td>
                                    <td>{statusBadge(c.status)}</td>
                                    <td style={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.notes}</td>
                                    <td>
                                        <div className="d-flex justify-content-end gap-2 align-items-center">
                                            {c.fundedAt ? (
                                                <span style={{ color: "#16a34a", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>
                                                    Funded {fmtDate(c.fundedAt)}
                                                </span>
                                            ) : c.status === "pending" ? (
                                                <button
                                                    className="btn btn-success btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                                                    disabled={this.hasAdjustEntries()}
                                                    onClick={() => this.applyEntryFunding(c)}
                                                >
                                                    Apply KTM Credit
                                                </button>
                                            ) : null}
                                            <button
                                                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                                                onClick={() => this.openEdit(c)}
                                            >
                                                <SquarePen size={15} /> Edit
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!this.state.loadingDetail && filteredEntries.length === 0 && (
                                <tr>
                                    <td colSpan={6} style={{ textAlign: "center", color: muted, padding: "24px 0" }}>
                                        {this.state.entries.length === 0 ? "No entries in this file." : "No entries match the search or filter."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <div style={{ marginTop: 36 }}>
                        <PageHeader
                            title="Activity Log"
                            subtitle="Full audit trail for this EFT file — generation, notifications, rejections, suspensions and credits"
                        />
                        <div>
                            {this.state.logs
                                .slice(this.state.logPage * PAGE_SIZE, (this.state.logPage + 1) * PAGE_SIZE)
                                .map((l) => (
                                    <div
                                        key={l.id}
                                        style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: "14px 4px", borderBottom: "1px solid #f1f5f9" }}
                                    >
                                        <div style={{ minWidth: 170 }}>{logBadge(l.action)}</div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ color: "#334155", wordBreak: "break-word" }}>{l.message}</div>
                                            <div style={{ fontSize: 12, color: muted, marginTop: 3 }}>
                                                {fmtDate(l.createdAt)} · {l.createdBy || "System"}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            {this.state.logs.length === 0 && (
                                <div style={{ color: muted, padding: "12px 4px" }}>No activity yet.</div>
                            )}
                            <Pager page={this.state.logPage} total={this.state.logs.length} onPage={(p) => this.setState({ logPage: p })} />
                        </div>
                    </div>

                    {this.renderEditModal()}
                </div>
            );
        }

        render() {
            return (
                <div>
                    {this.state.view === "list" ? this.renderList() : this.renderDetail()}
                    {this.renderConfigModal()}
                    {this.renderConfirmModal()}
                </div>
            );
        }
    }
);
